<template>
  <div class="page">
    <section
      class="page__item intro"
      @mousedown="handlerMousedown"
      @mouseup="handlerMouseup"
    >
      <AppTitle
        :data="currentData.title"
        class="page-item__title"
      />
      <div class="page-item__link page-item__link--dsk button--dsk" data-anim="button">
        <button
          aria-label="discover"
          @click="$eventHub.$emit('lockScroll', !$store.state.scrollIsLocked)"
          @mouseenter="cursorToScroll()"
          @mouseleave="cursorToClose()"
        >
          <span class="page-item__link-caption button__caption">Discover</span>
          <span class="page-item__arrow button__arrow">
            ›
          </span>
        </button>
      </div>
      <div class="page-item__link page-item__link--mbl button--mbl" data-anim="button">
        <button
          aria-label="discover"
          @click="$eventHub.$emit('lockScroll', !$store.state.scrollIsLocked)"
        >
          <span class="page-item__link-caption button__caption">Discover</span>
          <span class="page-item__arrow button__arrow">
            ›
          </span>
        </button>
      </div>
    </section>
    <section
      class="page__item content"
      ref="content"
      data-anim="content"
      @mousedown="handlerMousedown"
      @mouseup="handlerMouseup"
    >
      <p
        class="content__paragraph content__paragraph--first"
      >Scroll to read <span>🤓</span></p>
      <AppPicture
        class="content__image"
        :data="currentData.images[0]"
      />
      <p
        class="content__paragraph"
        data-animation="paragraph"
      >{{ currentData.paragraphs[0] }}</p>
      <AppPicture
        class="content__image"
        :data="currentData.images[1]"
      />
      <p
        class="content__paragraph"
        data-animation="paragraph"
      >{{ currentData.paragraphs[1] }}</p>
      <AppPicture
        class="content__image"
        :data="currentData.images[2]"
      />
    </section>
    <PageFooter
      :data="currentData.footer"
      ref="footer"
      data-animation="paragraph"
    />
  </div>
</template>

<script>
import { TweenMax } from 'gsap/TweenMax';
import Apollo from '@adoratorio/apollo';
import AppTitle from '@/components/AppTitle.vue';
import AppPicture from '@/components/AppPicture.vue';
import PageFooter from '@/components/PageFooter.vue';

import Cursor from '@/scripts/Cursor';
import { isTouchDevice } from '@/scripts/utils';
import loop from '@/scripts/loop';
import animations from '@/scripts/animations';
import initialStates from '@/scripts/animations/initialStates';
import reverse from '@/scripts/animations/reverse';

export default {
  name: 'page',

  components: {
    AppTitle,
    AppPicture,
    PageFooter,
  },

  data() {
    return {
      isTouchDevice,
      animationsAdded: false,
      handlerAnimations: null,
      ctaAdded: false,
      elsToSet: [],
    };
  },

  computed: {
    currentData() {
      const o = this.$store.state.content.find(p => p.year === this.$store.state.route.params.year);
      return o.content;
    },

    currentPage() {
      return this.$store.state.route.params.year;
    },
  },

  mounted() {
    TweenMax.set(this.$refs.content, { opacity: 0 });
    TweenMax.set(this.$refs.footer.$el, { autoAlpha: 0 });
    this.elsToAnimate = Array.from(this.$el.querySelectorAll('[data-animation]'));

    if (!this.$store.state.scrollIsLocked) {
      TweenMax.set('.page-item__link--dsk', { autoAlpha: 0 });
      setTimeout(() => {
        this.addAnimations();
      }, 200);
    }

    if (Cursor.ref !== null) {
      this.ctaAdded = true;
      Cursor.ref.addTargets([{
        id: 'apollo-page-cta',
        elements: Array.from(document.querySelectorAll('.button--dsk .button__arrow')),
        magnetism: {
          mode: Apollo.MODE.PULL,
          easing: {
            mode: Apollo.EASING.OUT_CUBIC,
            duration: 300,
          },
        },
      }]);
    }

    this.$eventHub.$on('pageIn', this.addAnimations);
    this.$eventHub.$on('pageOut', this.removeAnimations);
  },

  methods: {
    createObjToAnimate(el) {
      const obj = {
        node: el,
        fromTop() {
          return this.node.getBoundingClientRect().top;
        },
        animation: el.getAttribute('data-animation'),
        isAnimated: false,
        isVisible: false,
        splitted: false,
      };

      return obj;
    },

    addAnimationsPage(els) {
      els.forEach((el) => {
        if (!el.isAnimated && el.fromTop() < (window.innerHeight - 100)) {
          el.isAnimated = true; // eslint-disable-line
          el.isVisible = true; // eslint-disable-line

          animations.get(el.animation)(el.node);
        }
      });
    },

    initializeAnimations() {
      this.animationsAdded = true;
      const label = `animations-${this.currentPage}`;

      if (this.elsToAnimate.length > 0) {
        this.elsToSet = [];

        this.elsToAnimate.forEach((el) => {
          const obj = this.createObjToAnimate(el);
          this.elsToSet.push(obj);

          initialStates.get(obj.animation)(obj.node);
        });

        const handlerAnimations = () => this.addAnimationsPage(this.elsToSet, label);
        loop.add(handlerAnimations, label, true);
      }
    },

    addAnimations() {
      TweenMax.set('.page__item.content', { opacity: 1 });
      TweenMax.fromTo('.content__paragraph--first', 0.5, { autoAlpha: 0, delay: 0.6 }, { autoAlpha: 1 });
      this.initializeAnimations();
    },

    removeAnimations() {
      loop.remove(`animations-${this.currentPage}`);

      this.elsToSet.forEach((el, i) => {
        el.isAnimated = false; // eslint-disable-line
        el.isVisible = false; // eslint-disable-line

        reverse.get(el.animation)(el.node);
        if (i === this.elsToSet.length - 1) {
          TweenMax.to('.page__item.content', 0.5, { opacity: 0 });
        }
      });
    },

    handlerMousedown(event) {
      if (isTouchDevice()) return;
      this.$eventHub.$emit('mousedownPage', event);
    },

    handlerMouseup(event) {
      if (isTouchDevice()) return;
      this.$eventHub.$emit('mouseupPage', event);
    },

    cursorToScroll() {
      this.$eventHub.$emit('cursorToZoom');
      this.$eventHub.$emit('soundInteraction');
    },

    cursorToClose() {
      if (!this.$store.state.scrollIsLocked) {
        this.$eventHub.$emit('cursorToClose');
      } else {
        this.$eventHub.$emit('cursorToScroll');
      }
    },
  },

  beforeDestroy() {
    if (this.ctaAdded) Cursor.ref.removeTarget('apollo-page-cta');

    if (this.animationsAdded) {
      this.animationsAdded = false;
      this.$eventHub.$off('pageIn', this.addAnimations);
      this.$eventHub.$off('pageOut', this.removeAnimations);
    }
  },
};
</script>

<style lang="scss" scoped>
.page__item.intro {
  @include mq($until: ipadL) {
    height: 60vh;
    text-align: center;
    grid-template-rows: 65% auto;
  }
}
.page-item__title,
.page-item__link {
  position: relative;

  grid-column-start: 4;
  grid-column-end: 13;
  text-align: center;

  @include mq(ipadL) {
    grid-column-start: 13;
    grid-column-end: 26;
    text-align: left;
  }

  & > button {
    margin-top: 40px;

    background: transparent;
    border: none;
    font-size: font-size(xs);

    cursor: pointer;

    &:focus {
      outline: transparent;
    }
  }
}

.page-item__link--dsk {
  @include mq($until: ipadL) {
    @include visually-hidden;
  }
}

.page-item__link--mbl {
  @include mq(ipadL) {
    @include visually-hidden;
  }

  & .page-item__arrow::before {
    content: '';

    position: absolute;
    width: 65px;
    height: 65px;
    top: 50%;
    left: 50%;

    background-image: url('/img/svg/cursor.svg');
    background-position: center;
    background-size: contain;
    background-repeat: no-repeat;

    transform: translateX(-50%) translateY(-50%);
  }
}

.page-item__title {
  align-self: end;
}

.page-item__arrow {
  display: block;
  margin-top: 20px;
  padding: 20px;

  @include mq(ipadL) {
    display: inline-block;
    margin-top: 0;
    padding: 10px 20px;
    background: none;
  }

  font-size: font-size(xxs);
}

.page-item__link-caption {
  position: relative;
  display: inline-block;

  &::before {
    content: '';

    position: absolute;
    bottom: -5px;
    left: 0;
    width: 100%;
    height: 1px;

    background-color: $color-text;

    transform: scaleX(0);
    transform-origin: 0% 50%;
    transition: transform 0.5s $inOutQuint,
  }
}

.page-item__link button {
  @include mq($and: $is-not-touch-device) {
    &:hover .page-item__link-caption::before {
      transform: scaleX(1);
    }
  }
}

.page__item.content {
  perspective: 800px;

  @include mq(ipadL) {
    padding-top: 100px;
    padding-bottom: 80px;
  }

  @include mq(ds) {
    padding-top: 130px;
  }

  @include mq(dm) {
    padding-top: 200px;
  }

  p {
    font-size: font-size(xs);
    align-self: center;
    z-index: 5;

    @include mq(ipadL) {
      font-size: font-size(xxs);
    }
  }

  .content__image {
    z-index: -1;

    @include mq(ipadL) {
      margin: 80px 0;
    }

    @include mq(dm) {
      margin: 100px 0;
    }
  }

  & .content__paragraph {
    @include mq($until: ipadL) {
      padding: 60px 0;
      margin: 20px 0 0;
    }
  }

  & .content__paragraph:nth-of-type(1) {
    grid-column-start: 3;
    grid-column-end: 14;
    padding-top: 0;
    margin-top: 0;

    span {
      display: inline-block;
      font-size: font-size(m);
      transform: translateY(0.3rem);
    }

    @include mq($until: ipadL) {
      padding-top: 40px;
      text-align: center;
    }

    @include mq(ipadL) {
      grid-column-start: 19;
      grid-column-end: 27;
      grid-row-start: 1;
      text-align: center;
    }
  }

  & .content__paragraph:nth-of-type(2) {
    grid-column-start: 3;
    grid-column-end: 14;

    @include mq(ipadL) {
      grid-column-start: 7;
      grid-column-end: 15;
      grid-row-start: 2;
    }
  }

  & .content__paragraph:nth-of-type(3) {
    grid-column-start: 3;
    grid-column-end: 14;

    @include mq(ipadL) {
      margin-top: 0;
      grid-column-start: 19;
      grid-column-end: 27;
      grid-row-start: 3;
    }
  }

  & .content__image:nth-of-type(1) {
    grid-column-start: 1;
    grid-column-end: 12;
    margin-top: 0;


    @include mq(ipadL) {
      display: inline-block;
      grid-column-start: 6;
      grid-column-end: 16;
    }
  }

  & .content__image:nth-of-type(2) {
    grid-column-start: 5;
    grid-column-end: 18;

    @include mq(ipadL) {
      grid-column-start: 18;
      grid-column-end: 28;
    }
  }

  & .content__image:nth-of-type(3) {
    grid-column-start: 5;
    grid-column-end: 18;
    grid-column-start: 1;
    grid-column-end: 12;

    @include mq(ipadL) {
      grid-column-start: 6;
      grid-column-end: 16;
      grid-row-start: 3;
    }
  }

  & .content__image {
    &::before {
      content: '';

      position: absolute;
      top: 20px;
      width: 100%;
      height: 100%;

      border-bottom: solid 2px $color-primary;
    }
  }

  & figure:nth-of-type(even)::before {
    right: 20px;
    border-left: solid 2px $color-primary;

    transform-origin: 0% 100%;
    transform: scale(0);
    transition: transform 0.5s $inOutCirc;

    @include mq(ipadL) {
      right: unset;
      left: 20px;
      border-right: solid 2px $color-primary;
      border-left: 0;

      transform-origin: 100% 100%;
    }
  }

  & figure:nth-of-type(odd)::before {
    left: 20px;
    border-right: solid 2px $color-primary;

    transform-origin: 100% 100%;
    transform: scale(0);
    transition: transform 0.5s $inOutCirc;

    @include mq(ipadL) {
      left: unset;
      right: 20px;
      border-left: solid 2px $color-primary;
      border-right: 0;

      transform-origin: 0% 100%;
    }
  }
}
</style>

<style lang="scss">
.picture.content__image.picture--animated::before {
  transform: scale(1) !important;
}
</style>
